import React from 'react';
import { ArrowDownTrayIcon, ChevronLeftIcon, ChevronRightIcon, Squares2X2Icon } from '@heroicons/react/20/solid';
import { DeckThemeStyle, PublicDeck, ScaledSlide } from '../../../components/decks/render';
import { useDeckTracking } from '../useDeckTracking';
import { OverviewGrid, ThumbStrip, slideLabel } from './Overview';

interface Props {
    deck: PublicDeck;
    token: string;
    /** Tracking on (false for the dev sample). */
    track: boolean;
    /** Where "Download PDF" points. */
    pdfUrl: string;
}

const initials = (name: string): string => name.split(/\s+/).filter(Boolean).slice(0, 2).map((w) => w[0]?.toUpperCase() ?? '').join('');

// #region startIndex
function startIndex(count: number): number {
    const n = Number((window.location.hash.match(/^#(\d+)$/) ?? [])[1]);
    return Number.isFinite(n) && n >= 1 && n <= count ? n - 1 : 0;
}

// #region Presenter
/** The hosted deck: one slide at a time, full viewport, keyboard and swipe, filmstrip, overview, PDF and booking. */
export const Presenter: React.FunctionComponent<Props> = ({ deck, token, track, pdfUrl }): React.ReactElement => {
    const slides = React.useMemo(() => deck.doc.slides.filter((s) => !s.hidden), [deck]);
    const { theme, values } = deck.context;
    const [index, setIndex] = React.useState(() => startIndex(slides.length));
    const [overview, setOverview] = React.useState(false);
    const [pdfBusy, setPdfBusy] = React.useState(false);

    // #region downloadPdf
    /** The server's PDF; if it can't make one, the print view opens and the browser's "Save as PDF" takes over. */
    const downloadPdf = React.useCallback(async () => {
        const printView = `${window.location.pathname}?print=1&autoprint=1`;
        if (!/\/pdf$/.test(pdfUrl)) { window.open(printView, '_blank', 'noopener'); return; }
        setPdfBusy(true);
        try {
            const res = await fetch(pdfUrl, { credentials: 'omit' });
            if (!res.ok || !(res.headers.get('content-type') ?? '').includes('pdf')) throw new Error(String(res.status));
            const blob = await res.blob();
            const name = /filename="([^"]+)"/.exec(res.headers.get('content-disposition') ?? '')?.[1] ?? `${deck.title || 'deck'}.pdf`;
            const url = URL.createObjectURL(blob);
            const a = document.createElement('a');
            a.href = url; a.download = name; document.body.appendChild(a); a.click(); a.remove();
            setTimeout(() => URL.revokeObjectURL(url), 30_000);
        } catch {
            // Same tab (no pop-up blocker): print view, browser print dialog, then back to the deck.
            window.location.assign(printView);
        } finally {
            setPdfBusy(false);
        }
    }, [pdfUrl, deck.title]);
    const current = slides[index] ?? null;
    const { track: send } = useDeckTracking(token, track, current?.id ?? null);
    const touch = React.useRef<{ x: number; y: number } | null>(null);

    const go = React.useCallback((i: number) => setIndex(Math.max(0, Math.min(slides.length - 1, i))), [slides.length]);

    // The slide number lives in the hash (#3), so a reload or a shared link opens on the same slide.
    React.useEffect(() => { window.history.replaceState(null, '', `#${index + 1}`); }, [index]);
    React.useEffect(() => {
        const onHash = (): void => setIndex(startIndex(slides.length));
        window.addEventListener('hashchange', onHash);
        return () => window.removeEventListener('hashchange', onHash);
    }, [slides.length]);

    React.useEffect(() => {
        const onKey = (e: KeyboardEvent): void => {
            if (overview || e.metaKey || e.ctrlKey || e.altKey) return;
            const t = e.target as HTMLElement | null;
            if (t && (t.isContentEditable || /^(INPUT|TEXTAREA|SELECT)$/.test(t.tagName))) return;
            if (e.key === 'ArrowRight' || e.key === 'PageDown' || (e.key === ' ' && !e.shiftKey)) { e.preventDefault(); go(index + 1); }
            else if (e.key === 'ArrowLeft' || e.key === 'PageUp' || (e.key === ' ' && e.shiftKey)) { e.preventDefault(); go(index - 1); }
            else if (e.key === 'Home') go(0);
            else if (e.key === 'End') go(slides.length - 1);
        };
        window.addEventListener('keydown', onKey);
        return () => window.removeEventListener('keydown', onKey);
    }, [index, overview, go, slides.length]);

    const onCta = (url: string): void => { send('cta'); window.open(url, '_blank', 'noopener'); };
    const booking = deck.rep?.booking_link || values['rep.booking_link'] || '';
    const progress = slides.length > 1 ? (index / (slides.length - 1)) * 100 : 100;
    const label = current ? slideLabel(current, values, index) : '';

    return (
        <div className="wd-themed fixed inset-0 flex flex-col overflow-hidden text-[var(--wd-ink)]"
            style={{ background: 'color-mix(in srgb, var(--wd-ink) 7%, var(--wd-page))', fontFamily: 'var(--wd-font)' }}>
            <DeckThemeStyle theme={theme} />
            <header className="flex h-14 flex-none items-center gap-3 border-b border-black/5 bg-[var(--wd-page)] px-3 sm:h-16 sm:gap-4 sm:px-6">
                {theme.logo_url ? <img src={theme.logo_url} alt={theme.name} className="h-6 w-auto max-w-[120px] object-contain sm:h-7 sm:max-w-[160px]" /> : null}
                {deck.prepared_for ? (
                    <p className="min-w-0 truncate border-l border-black/10 pl-3 text-[13px] text-[var(--wd-muted)] sm:pl-4 sm:text-sm">
                        Prepared for <span className="font-semibold text-[var(--wd-ink)]">{deck.prepared_for}</span>
                    </p>
                ) : null}
                <div className="flex-1" />
                {deck.rep ? (
                    <div className="hidden items-center gap-2.5 md:flex" title={deck.rep.title ?? undefined}>
                        {deck.rep.photo_url
                            ? <img src={deck.rep.photo_url} alt="" className="h-8 w-8 rounded-full object-cover" />
                            : <span className="flex h-8 w-8 items-center justify-center rounded-full bg-[var(--wd-accent)] text-xs font-semibold text-white">{initials(deck.rep.name)}</span>}
                        <span className="text-sm leading-tight"><span className="block font-semibold">{deck.rep.name}</span>{deck.rep.title ? <span className="block text-xs text-[var(--wd-muted)]">{deck.rep.title}</span> : null}</span>
                    </div>
                ) : null}
                <button type="button" disabled={pdfBusy} aria-busy={pdfBusy} onClick={() => { send('pdf'); void downloadPdf(); }}
                    className="inline-flex h-9 items-center gap-1.5 rounded-full px-2.5 text-sm font-semibold text-[var(--wd-ink)] hover:bg-black/5 disabled:opacity-60 focus-visible:outline focus-visible:outline-2 focus-visible:outline-[var(--wd-accent)] sm:px-3.5">
                    <ArrowDownTrayIcon className="h-5 w-5" aria-hidden="true" /><span className="hidden sm:inline">{pdfBusy ? 'Preparing PDF…' : 'Download PDF'}</span><span className="sr-only sm:hidden">Download PDF</span>
                </button>
                <button type="button" onClick={() => setOverview(true)} aria-label="All slides"
                    className="inline-flex h-9 w-9 items-center justify-center rounded-full text-[var(--wd-ink)] hover:bg-black/5 focus-visible:outline focus-visible:outline-2 focus-visible:outline-[var(--wd-accent)]">
                    <Squares2X2Icon className="h-5 w-5" aria-hidden="true" />
                </button>
            </header>

            <main className="relative min-h-0 flex-1 px-3 py-4 sm:px-20 sm:py-8" aria-roledescription="slide deck" aria-label={deck.title}
                onTouchStart={(e) => { const t = e.touches[0]; touch.current = { x: t.clientX, y: t.clientY }; }}
                onTouchEnd={(e) => {
                    const s = touch.current; touch.current = null;
                    const t = e.changedTouches[0];
                    if (!s || !t) return;
                    const dx = t.clientX - s.x; const dy = t.clientY - s.y;
                    if (Math.abs(dx) > 45 && Math.abs(dx) > Math.abs(dy) * 1.3) go(index + (dx < 0 ? 1 : -1));
                }}>
                {current ? (
                    <div className="h-full w-full" role="group" aria-roledescription="slide" aria-label={`${index + 1} of ${slides.length}: ${label}`}>
                        <ScaledSlide key={current.id} slide={current} context={deck.context} mode="view" fit="contain"
                            onAction={() => send('cta', current.id)}
                            innerStyle={{ borderRadius: 28, overflow: 'hidden', boxShadow: '0 40px 120px -30px rgba(10,20,15,.35), 0 0 0 1px rgba(10,20,15,.06)' }} />
                    </div>
                ) : null}
                {index === 0 && slides.length > 1 ? (
                    <p className="pointer-events-none absolute inset-x-0 bottom-6 text-center text-[13px] text-[var(--wd-muted)] sm:hidden">Swipe for the next slide · turn your phone for a bigger view</p>
                ) : null}
                <NavButton side="left" disabled={index === 0} onClick={() => go(index - 1)} />
                <NavButton side="right" disabled={index >= slides.length - 1} onClick={() => go(index + 1)} />
            </main>

            <footer className="flex-none bg-[var(--wd-page)]">
                <div className="h-[3px] w-full bg-black/5" aria-hidden="true">
                    <div className="h-full bg-[var(--wd-accent)] transition-[width] duration-300 ease-out" style={{ width: `${progress}%` }} />
                </div>
                <div className="flex items-center gap-3 px-3 py-2 sm:gap-5 sm:px-6">
                    <p className="flex-none text-sm tabular-nums text-[var(--wd-muted)]" aria-live="polite">
                        <span className="font-semibold text-[var(--wd-ink)]">{index + 1}</span> / {slides.length}
                    </p>
                    <div className="hidden min-w-0 flex-1 lg:flex">
                        <ThumbStrip slides={slides} context={deck.context} index={index} onPick={go} />
                    </div>
                    <p className="min-w-0 flex-1 truncate text-sm text-[var(--wd-muted)] lg:hidden">{label}</p>
                    {booking ? (
                        <button type="button" onClick={() => onCta(booking)}
                            className="inline-flex h-10 flex-none items-center justify-center rounded-full bg-[var(--wd-accent)] px-4 text-sm font-semibold text-white shadow-sm hover:brightness-110 focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-[var(--wd-accent)] sm:h-11 sm:px-6">
                            Book a call
                        </button>
                    ) : null}
                </div>
            </footer>

            {overview ? <OverviewGrid slides={slides} context={deck.context} index={index} onPick={(i) => { go(i); setOverview(false); }} onClose={() => setOverview(false)} /> : null}
        </div>
    );
};

// #region NavButton
const NavButton: React.FunctionComponent<{ side: 'left' | 'right'; disabled: boolean; onClick: () => void }> = ({ side, disabled, onClick }): React.ReactElement => {
    const Icon = side === 'left' ? ChevronLeftIcon : ChevronRightIcon;
    return (
        <button type="button" onClick={onClick} disabled={disabled} aria-label={side === 'left' ? 'Previous slide' : 'Next slide'}
            className={`absolute top-1/2 hidden h-12 w-12 -translate-y-1/2 items-center justify-center rounded-full bg-[var(--wd-page)] text-[var(--wd-ink)] shadow-md ring-1 ring-black/5 transition hover:scale-105 disabled:pointer-events-none disabled:opacity-0 focus-visible:outline focus-visible:outline-2 focus-visible:outline-[var(--wd-accent)] sm:flex ${side === 'left' ? 'left-4' : 'right-4'}`}>
            <Icon className="h-6 w-6" aria-hidden="true" />
        </button>
    );
};
